/**
 * Parser for Taiwan electronic uniform invoice (電子發票) QR codes.
 *
 * Each 電子發票 carries two QR codes. The LEFT QR encodes a fixed-length header
 * followed by ":"-separated item details. The header holds the invoice number,
 * date (民國/ROC) and the tax-inclusive total; the tail holds the line items,
 * from which we best-effort extract the first 品名 as a description.
 *
 * Left QR header layout (fixed offsets):
 *   [0..10)   invoice number          e.g. "AB12345678"
 *   [10..17)  ROC date  YYYMMDD       e.g. "1130410" -> 民國113年04月10日
 *   [17..21)  random code (隨機碼)
 *   [21..29)  sales amount, untaxed   8 hex digits
 *   [29..37)  total amount, taxed     8 hex digits  <-- what we want
 *   [37..45)  buyer tax id            "00000000" if none
 *   [45..53)  seller tax id
 *   [53..77)  AES verification (base64)
 *   then ":" separated: 自行使用區 : 完整筆數 : 總筆數 : 中文編碼 : 品名 : 數量 : 單價 : ...
 *     (the 自行使用區 / seller own-use field may be empty but is always present)
 *
 * The RIGHT QR is a continuation (starts with "**") and is not needed here.
 */

const INVOICE_NUMBER_RE = /^[A-Z]{2}\d{8}$/;

// Form 款項 input caps at 12 chars; keep prefilled names within that.
const MAX_DESCRIPTION_LEN = 12;

// A name is usable only if it decoded to readable text — a wrong charset
// produces replacement chars (�) or control bytes, which we reject.
function isReadableText(s) {
  if (!s) return false;
  if (s.indexOf('�') !== -1) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/.test(s)) return false;
  return s.trim().length > 0;
}

function decodeBytes(bytes, label) {
  try {
    if (typeof TextDecoder === 'undefined') return null;
    const out = new TextDecoder(label, { fatal: false }).decode(bytes);
    return isReadableText(out) ? out : null;
  } catch (e) {
    return null;
  }
}

function base64ToBytes(b64) {
  try {
    if (typeof atob !== 'function') return null;
    const bin = atob(b64);
    return Uint8Array.from(bin, (c) => c.charCodeAt(0));
  } catch (e) {
    return null;
  }
}

// Best-effort first item name (品名). The tail after the 77-char header is
// ":"-separated: [自行使用區, 完整筆數, 總筆數, 中文編碼, 品名, 數量, 單價, ...].
// 中文編碼: '0' Big5, '1' UTF-8, '2' Base64. Returns null when unreadable.
function extractDescription(segments) {
  if (!segments || segments.length < 6) return null;
  const encoding = segments[4];
  const rawName = (segments[5] || '').trim();
  if (!rawName) return null;

  let name = null;
  if (encoding === '2') {
    // Base64-encoded name. Decode first; only fall back to the literal value
    // (already-plain text mislabelled as base64) when the decode is unreadable.
    const bytes = base64ToBytes(rawName);
    name = bytes ? (decodeBytes(bytes, 'utf-8') || decodeBytes(bytes, 'big5')) : null;
    if (!name && isReadableText(rawName)) name = rawName;
  } else if (isReadableText(rawName)) {
    // The scanner usually hands us already-decoded text (UTF-8); trust it.
    name = rawName;
  } else {
    // Byte-mode read as Latin-1 by the scanner — re-read those bytes as Big5.
    const bytes = Uint8Array.from(rawName, (c) => c.charCodeAt(0) & 0xff);
    name = decodeBytes(bytes, 'big5');
  }

  if (!name) return null;
  name = name.trim();
  return name ? name.slice(0, MAX_DESCRIPTION_LEN) : null;
}

function parseRocDate(roc) {
  // roc is YYYMMDD where YYY is the ROC year (民國). 113 -> 2024.
  if (!/^\d{7}$/.test(roc)) return null;
  const rocYear = parseInt(roc.slice(0, 3), 10);
  const month = parseInt(roc.slice(3, 5), 10);
  const day = parseInt(roc.slice(5, 7), 10);
  const year = rocYear + 1911;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/**
 * Parse the LEFT QR payload of a Taiwan e-invoice.
 * @param {string} raw - decoded QR string.
 * @returns {{
 *   invoiceNumber: string,
 *   date: { year: number, month: number, day: number } | null,
 *   isoDate: string | null,
 *   amount: number,
 *   salesAmount: number,
 *   sellerTaxId: string,
 *   buyerTaxId: string,
 *   description: string | null,
 * } | null}  null when the payload is not a recognisable left-QR header.
 */
function parseInvoiceQr(raw) {
  if (typeof raw !== 'string') return null;
  const text = raw.trim();

  // Right QR (continuation) cannot be parsed on its own.
  if (text.startsWith('**')) return null;

  const segments = text.split(':');
  // Header up to the first ":" must be at least 77 chars to hold all fixed fields.
  const header = segments[0];
  if (header.length < 77) return null;

  const invoiceNumber = header.slice(0, 10);
  if (!INVOICE_NUMBER_RE.test(invoiceNumber)) return null;

  const date = parseRocDate(header.slice(10, 17));

  const salesHex = header.slice(21, 29);
  const totalHex = header.slice(29, 37);
  if (!/^[0-9A-Fa-f]{8}$/.test(totalHex)) return null;

  const amount = parseInt(totalHex, 16);
  const salesAmount = parseInt(salesHex, 16);

  const buyerTaxId = header.slice(37, 45);
  const sellerTaxId = header.slice(45, 53);

  const isoDate = date
    ? `${String(date.year).padStart(4, '0')}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`
    : null;

  const description = extractDescription(segments);

  return {
    invoiceNumber,
    date,
    isoDate,
    amount,
    salesAmount,
    sellerTaxId,
    buyerTaxId,
    description,
  };
}

export { parseInvoiceQr, parseRocDate };
